<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import { RefreshCw, Send } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import {
  useCampaignService,
  type ICampaignKnowledgeChatSource,
} from '@/services/campaign/useCampaignService'

type KnowledgeChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: ICampaignKnowledgeChatSource[]
  warning?: string | null
}

const props = defineProps<{
  campaignId?: string | null
  context: string
  readyDocumentsCount: number
}>()

const visible = defineModel<boolean>({ default: false })

const campaignService = useCampaignService()
const { t } = useI18n()
const draft = ref('')
const loading = ref(false)
const messages = ref<KnowledgeChatMessage[]>([])
const messagesElement = ref<HTMLElement | null>(null)

const storageKey = computed(() =>
  props.campaignId ? `knowledge-chat:${props.context}:${props.campaignId}` : '',
)

const uniqueSources = (sources: ICampaignKnowledgeChatSource[] = []) => {
  const seen = new Set<string>()
  return sources.filter((source) => {
    const key = source.document_id || source.title
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const persistMessages = () => {
  if (!storageKey.value) return
  localStorage.setItem(storageKey.value, JSON.stringify(messages.value))
}

const loadMessages = () => {
  if (!storageKey.value) {
    messages.value = []
    return
  }

  try {
    const raw = localStorage.getItem(storageKey.value)
    const parsed = raw ? JSON.parse(raw) : []
    messages.value = Array.isArray(parsed) ? parsed : []
  } catch {
    messages.value = []
  }
}

const scrollToBottom = async () => {
  await nextTick()
  messagesElement.value?.scrollTo({
    top: messagesElement.value.scrollHeight,
    behavior: 'smooth',
  })
}

const sendQuestion = async (questionFromCard?: string) => {
  const question = (questionFromCard ?? draft.value).trim()
  if (!props.campaignId || !question || loading.value || props.readyDocumentsCount === 0) return

  visible.value = true
  draft.value = ''
  messages.value.push({
    id: crypto.randomUUID(),
    role: 'user',
    content: question,
  })
  persistMessages()

  loading.value = true
  try {
    const response = await campaignService.askCampaignKnowledgeQuestion(props.campaignId, question)
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: response.answer,
      sources: uniqueSources(response.sources),
      warning: response.warning,
    })
  } catch {
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: t('ai.knowledge_chat.request_failed'),
      warning: 'knowledge_chat_request_failed',
    })
  } finally {
    loading.value = false
    persistMessages()
    void scrollToBottom()
  }
}

defineExpose({
  sendQuestion,
})

watch(storageKey, () => {
  draft.value = ''
  loadMessages()
}, { immediate: true })

watch(visible, (isVisible) => {
  if (isVisible) void scrollToBottom()
})
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="t('ai.knowledge_chat.title')"
    :modal="false"
    :draggable="true"
    :dismissable-mask="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '575px': '94vw' }"
  >
    <section class="flex h-[70vh] flex-col gap-3">
      <div
        ref="messagesElement"
        class="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950"
      >
        <p
          v-if="!messages.length"
          class="rounded-md border border-dashed border-slate-300 bg-white p-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          {{ t('ai.knowledge_chat.empty_hint') }}
        </p>

        <div
          v-for="message in messages"
          :key="message.id"
          class="flex"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-5"
            :class="
              message.role === 'user'
                ? 'bg-[var(--p-primary-color)] text-white'
                : 'border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
            "
          >
            {{ message.content }}
            <div v-if="message.sources?.length" class="mt-2 flex flex-wrap gap-1">
              <span
                v-for="source in uniqueSources(message.sources)"
                :key="`${message.id}-${source.document_id}`"
                class="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900"
              >
                {{ source.title }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="loading" class="flex justify-start">
          <div class="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
            <span class="text-xs text-slate-500 dark:text-slate-300">
              {{ t('ai.knowledge_chat.consulting') }}
            </span>
          </div>
        </div>
      </div>

      <form class="flex items-end gap-2" @submit.prevent="sendQuestion()">
        <textarea
          v-model="draft"
          rows="2"
          class="min-w-0 flex-1 resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-[var(--p-primary-color)] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          :placeholder="t('ai.knowledge_chat.question_placeholder')"
          :disabled="loading || readyDocumentsCount === 0"
          @keydown.enter.exact.prevent="sendQuestion()"
        />
        <button
          type="submit"
          class="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-[var(--p-primary-color)] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="loading || !draft.trim() || readyDocumentsCount === 0"
          :aria-label="t('ai.cards_panel.send_question')"
        >
          <RefreshCw v-if="loading" class="h-4 w-4 animate-spin" />
          <Send v-else class="h-4 w-4" />
        </button>
      </form>
    </section>
  </Dialog>
</template>
